import { supabase } from '@/integrations/supabase/client';
import { getHubSpotClient } from './hubspotClient';

export interface SyncOptions {
  entityType: 'contact' | 'company' | 'deal' | 'engagement' | 'all';
  syncType: 'manual' | 'scheduled' | 'webhook' | 'initial';
  direction?: 'hubspot_to_crm' | 'crm_to_hubspot' | 'bidirectional';
  batchSize?: number;
}

export interface SyncProgress {
  syncId: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  entityType: string;
  recordsProcessed: number;
  recordsCreated: number;
  recordsUpdated: number;
  recordsFailed: number;
  totalRecords?: number;
  progress: number;
  message: string;
  errors?: any[];
}

export class HubSpotSyncService {
  private client: any;
  private syncId: string | null = null;

  async initialize() {
    this.client = await getHubSpotClient();
  }

  async startSync(options: SyncOptions): Promise<string> {
    // Create sync history record
    const { data: syncHistory, error } = await supabase
      .from('hubspot_sync_history')
      .insert({
        sync_type: options.syncType,
        entity_type: options.entityType,
        status: 'pending',
        direction: options.direction || 'hubspot_to_crm',
      })
      .select()
      .single();

    if (error) throw error;

    this.syncId = syncHistory.id;

    // Start sync based on entity type
    if (options.entityType === 'all') {
      await this.syncAllEntities(options);
    } else {
      await this.syncEntity(options.entityType, options);
    }

    return this.syncId;
  }

  private async syncAllEntities(options: SyncOptions) {
    const entities: Array<'contact' | 'company' | 'deal' | 'engagement'> = [
      'contact',
      'company',
      'deal',
      'engagement',
    ];

    for (const entity of entities) {
      await this.syncEntity(entity, { ...options, entityType: entity });
    }
  }

  private async syncEntity(
    entityType: 'contact' | 'company' | 'deal' | 'engagement',
    options: SyncOptions
  ) {
    try {
      await this.updateSyncStatus('running', `Syncing ${entityType}s...`);

      switch (entityType) {
        case 'contact':
          await this.syncContacts(options);
          break;
        case 'company':
          await this.syncCompanies(options);
          break;
        case 'deal':
          await this.syncDeals(options);
          break;
        case 'engagement':
          await this.syncEngagements(options);
          break;
      }

      await this.updateSyncStatus('completed', `${entityType} sync completed`);
    } catch (error) {
      await this.updateSyncStatus('failed', `${entityType} sync failed`, error);
      throw error;
    }
  }

  private async syncContacts(options: SyncOptions) {
    const batchSize = options.batchSize || 100;
    let after: string | undefined;
    let totalProcessed = 0;
    let created = 0;
    let updated = 0;
    let failed = 0;

    do {
      try {
        // Fetch contacts from HubSpot
        const response = await this.client.getContacts(batchSize, after);
        const contacts = response.results;

        // Process each contact
        for (const hubspotContact of contacts) {
          try {
            await this.processContact(hubspotContact);
            totalProcessed++;

            // Check if contact exists
            const { data: existingContact } = await supabase
              .from('contacts')
              .select('id')
              .eq('hubspot_id', hubspotContact.id)
              .single();

            if (existingContact) {
              updated++;
            } else {
              created++;
            }
          } catch (error) {
            failed++;
          }
        }

        // Update progress
        await this.updateSyncProgress({
          recordsProcessed: totalProcessed,
          recordsCreated: created,
          recordsUpdated: updated,
          recordsFailed: failed,
          progress: response.paging?.next ? 50 : 100,
          message: `Processed ${totalProcessed} contacts`,
        });

        after = response.paging?.next?.after;
      } catch (error) {
        throw error;
      }
    } while (after);
  }

  async processContact(hubspotContact: any) {
    const properties = hubspotContact.properties;

    // Map HubSpot contact to CRM contact
    const contactData = {
      hubspot_id: hubspotContact.id,
      email: properties.email,
      first_name: properties.firstname,
      last_name: properties.lastname,
      phone: properties.phone,
      job_title: properties.jobtitle,
      hubspot_sync_date: new Date().toISOString(),
    };

    // Find organization by domain if company exists
    let organizationId = null;
    if (properties.email) {
      const domain = properties.email.split('@')[1];
      const { data: org } = await supabase
        .from('organizations')
        .select('id')
        .eq('website', `https://${domain}`)
        .single();

      if (org) {
        organizationId = org.id;
      }
    }

    if (organizationId) {
      Object.assign(contactData, { organization_id: organizationId });
    }

    // Upsert contact
    const { error } = await supabase.from('contacts').upsert(contactData, {
      onConflict: 'hubspot_id',
      ignoreDuplicates: false,
    });

    if (error) throw error;

    // Track sync
    await supabase.from('hubspot_entity_sync').upsert(
      {
        entity_type: 'contact',
        hubspot_id: hubspotContact.id,
        local_id: contactData.email, // We'll update this after getting the actual ID
        sync_status: 'synced',
      },
      {
        onConflict: 'entity_type,hubspot_id',
      }
    );
  }

  private async syncCompanies(options: SyncOptions) {
    const batchSize = options.batchSize || 100;
    let after: string | undefined;
    let totalProcessed = 0;
    let created = 0;
    let updated = 0;
    let failed = 0;

    do {
      try {
        const response = await this.client.getCompanies(batchSize, after);
        const companies = response.results;

        for (const hubspotCompany of companies) {
          try {
            await this.processCompany(hubspotCompany);
            totalProcessed++;

            const { data: existingOrg } = await supabase
              .from('organizations')
              .select('id')
              .eq('hubspot_id', hubspotCompany.id)
              .single();

            if (existingOrg) {
              updated++;
            } else {
              created++;
            }
          } catch (error) {
            failed++;
          }
        }

        await this.updateSyncProgress({
          recordsProcessed: totalProcessed,
          recordsCreated: created,
          recordsUpdated: updated,
          recordsFailed: failed,
          progress: response.paging?.next ? 50 : 100,
          message: `Processed ${totalProcessed} companies`,
        });

        after = response.paging?.next?.after;
      } catch (error) {
        throw error;
      }
    } while (after);
  }

  async processCompany(hubspotCompany: any) {
    const properties = hubspotCompany.properties;

    const orgData = {
      hubspot_id: hubspotCompany.id,
      name: properties.name,
      website: properties.domain ? `https://${properties.domain}` : null,
      industry: properties.industry,
      employee_count: properties.numberofemployees ? parseInt(properties.numberofemployees) : null,
      city: properties.city,
      state: properties.state,
      country: properties.country,
      hubspot_sync_date: new Date().toISOString(),
    };

    const { error } = await supabase.from('organizations').upsert(orgData, {
      onConflict: 'hubspot_id',
      ignoreDuplicates: false,
    });

    if (error) throw error;

    await supabase.from('hubspot_entity_sync').upsert(
      {
        entity_type: 'company',
        hubspot_id: hubspotCompany.id,
        sync_status: 'synced',
      },
      {
        onConflict: 'entity_type,hubspot_id',
      }
    );
  }

  private async syncDeals(options: SyncOptions) {
    const batchSize = options.batchSize || 100;
    let after: string | undefined;
    let totalProcessed = 0;
    let created = 0;
    let updated = 0;
    let failed = 0;

    do {
      try {
        const response = await this.client.getDeals(batchSize, after);
        const deals = response.results;

        for (const hubspotDeal of deals) {
          try {
            await this.processDeal(hubspotDeal);
            totalProcessed++;

            const { data: existingOpp } = await supabase
              .from('opportunities')
              .select('id')
              .eq('hubspot_deal_id', hubspotDeal.id)
              .single();

            if (existingOpp) {
              updated++;
            } else {
              created++;
            }
          } catch (error) {
            failed++;
          }
        }

        await this.updateSyncProgress({
          recordsProcessed: totalProcessed,
          recordsCreated: created,
          recordsUpdated: updated,
          recordsFailed: failed,
          progress: response.paging?.next ? 50 : 100,
          message: `Processed ${totalProcessed} deals`,
        });

        after = response.paging?.next?.after;
      } catch (error) {
        throw error;
      }
    } while (after);
  }

  async processDeal(hubspotDeal: any) {
    const properties = hubspotDeal.properties;

    // Map deal stage to opportunity stage
    const stageMapping: Record<string, string> = {
      appointmentscheduled: 'qualified',
      qualifiedtobuy: 'qualified',
      presentationscheduled: 'proposal',
      decisionmakerboughtin: 'negotiation',
      contractsent: 'negotiation',
      closedwon: 'closed-won',
      closedlost: 'closed-lost',
    };

    const oppData = {
      hubspot_deal_id: hubspotDeal.id,
      name: properties.dealname,
      amount: properties.amount ? parseFloat(properties.amount) : 0,
      stage: stageMapping[properties.dealstage] || 'prospecting',
      close_date: properties.closedate,
      hubspot_sync_date: new Date().toISOString(),
    };

    // Get associations to find the company
    try {
      const associations = await this.client.getAssociations('deals', hubspotDeal.id, 'companies');
      if (associations.results?.length > 0) {
        const companyId = associations.results[0].id;
        const { data: org } = await supabase
          .from('organizations')
          .select('id')
          .eq('hubspot_id', companyId)
          .single();

        if (org) {
          Object.assign(oppData, { organization_id: org.id });
        }
      }
    } catch (error) {
      // Error handling omitted intentionally
    }

    const { error } = await supabase.from('opportunities').upsert(oppData, {
      onConflict: 'hubspot_deal_id',
      ignoreDuplicates: false,
    });

    if (error) throw error;

    await supabase.from('hubspot_entity_sync').upsert(
      {
        entity_type: 'deal',
        hubspot_id: hubspotDeal.id,
        sync_status: 'synced',
      },
      {
        onConflict: 'entity_type,hubspot_id',
      }
    );
  }

  private async syncEngagements(options: SyncOptions) {
    // Similar implementation for engagements/activities
  }

  private async updateSyncStatus(status: string, message: string, error?: any) {
    if (!this.syncId) return;

    const updateData: any = {
      status,
      metadata: { message },
    };

    if (status === 'completed') {
      updateData.completed_at = new Date().toISOString();
    }

    if (error) {
      updateData.error_details = {
        message: error.message,
        stack: error.stack,
      };
    }

    await supabase.from('hubspot_sync_history').update(updateData).eq('id', this.syncId);
  }

  private async updateSyncProgress(progress: Partial<SyncProgress>) {
    if (!this.syncId) return;

    await supabase
      .from('hubspot_sync_history')
      .update({
        records_processed: progress.recordsProcessed,
        records_created: progress.recordsCreated,
        records_updated: progress.recordsUpdated,
        records_failed: progress.recordsFailed,
        metadata: {
          progress: progress.progress,
          message: progress.message,
        },
      })
      .eq('id', this.syncId);
  }

  async getSyncStatus(syncId: string): Promise<SyncProgress | null> {
    const { data } = await supabase
      .from('hubspot_sync_history')
      .select('*')
      .eq('id', syncId)
      .single();

    if (!data) return null;

    return {
      syncId: data.id,
      status: data.status,
      entityType: data.entity_type,
      recordsProcessed: data.records_processed,
      recordsCreated: data.records_created,
      recordsUpdated: data.records_updated,
      recordsFailed: data.records_failed,
      progress: data.metadata?.progress || 0,
      message: data.metadata?.message || '',
    };
  }
}

export const hubSpotSyncService = new HubSpotSyncService();
